import { mergeProps, Show } from 'solid-js';
import ApplicationActionsDropdown from './ApplicationActionsDropdown';
import { RightSectionHeader } from '~/components/Layout';
import { Button } from '~/components/ui/Button';
import { Localized } from '~/i18n';
import {
  generateRedirectionToApplicationListURL,
  getTranslationKey,
  handleDeleteApplication,
  MultiApplicationFlowType
} from './utils';
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-solid';
import { ReadOnlyField } from '~/components/ui/ReadOnlyField';
import { ApiConfigData, EApplicationType } from '~/api';
import { useConfirm } from '~/components/ui/Modal';
import { useToast } from '~/components/ui/Toast';
import { useIntl } from '@cookbook/solid-intl';
import { useQueryClient } from '@tanstack/solid-query';
import { useAppData } from '~/components/AppData';
import { useNavigate } from '@solidjs/router';
import MultiAppIcons from './MultiAppIcons';
import Alert from '~/components/ui/Alert';

type MetaApplicationInfoAndActionsProps = {
  applicationData: ApiConfigData;
};

export default function MetaApplicationInfoAndActions(
  props: MetaApplicationInfoAndActionsProps
) {
  const merged = mergeProps({}, props);
  const confirm = useConfirm();
  const toaster = useToast();
  const intl = useIntl();
  const queryClient = useQueryClient();
  const appData = useAppData();
  const navigate = useNavigate();

  return (
    <RightSectionHeader
      headerContent={
        <div class="flex flex-col gap-5">
          <div class={`flex gap-4 items-center w-full`}>
            <div class={`flex items-center gap-3`}>
              <div
                class={`p-[10px] bg-bg-muted rounded-lg border border-border-default`}
              >
                <MultiAppIcons
                  applicationType={
                    merged.applicationData.clientType as EApplicationType
                  }
                  flowType={MultiApplicationFlowType.EDIT_APPLICATION}
                />
              </div>
              <div class={`flex flex-col gap-2 justify-center`}>
                <div class={`text-xl font-medium`}>
                  {merged.applicationData.name}
                </div>
                <div class="flex items-center gap-2 text-sm font-normal">
                  <ReadOnlyField
                    value={merged.applicationData.keyId}
                    containerClass={`border-none h-6 text-sm text-fg-default`}
                    iconClass={`!w-4 !h-4`}
                  />
                </div>
              </div>
            </div>
            <Show
              when={
                (merged.applicationData.clientType as EApplicationType) !==
                EApplicationType.DEFAULT_APPLICATION
              }
            >
              <div class={`ml-auto`}>
                <ApplicationActionsDropdown
                  appData={merged.applicationData}
                  dropdownId="multi-app-actions-dropdown"
                  deleteLabelKey="actions_delete"
                  onDelete={async () => {
                    const deleted = await handleDeleteApplication({
                      data: merged.applicationData,
                      intl,
                      confirm,
                      queryClient,
                      toaster
                    });
                    if (deleted) {
                      navigate(
                        generateRedirectionToApplicationListURL(
                          appData.selectedEnvironment?.id!
                        )
                      );
                    }
                  }}
                  renderTrigger={(isOpen) => (
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid="multi-app-actions-dropdown-trigger"
                    >
                      <Localized
                        translationKey={getTranslationKey(
                          'dropdown_action_cta'
                        )}
                      />
                      <Show
                        when={isOpen}
                        fallback={<ChevronDownIcon class="ml-1.5" />}
                      >
                        <ChevronUpIcon class="ml-1.5" />
                      </Show>
                    </Button>
                  )}
                />
              </div>
            </Show>
          </div>
          <Show
            when={
              (merged.applicationData.clientType as EApplicationType) ===
              EApplicationType.DEFAULT_APPLICATION
            }
          >
            <Alert variant={'info'}>
              <Localized
                translationKey={getTranslationKey(
                  'default_application_info_message'
                )}
              />
            </Alert>
          </Show>
        </div>
      }
      class={`mt-4`}
    />
  );
}
